import React, { useState, useEffect } from 'react';
import { Layout } from '../../components/layout/Layout';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { Modal } from '../../components/ui/Modal';
import { Plus, Check, Users, BookOpen, Building2, Play, Square } from 'lucide-react';
import { listPlans, activatePlan, deactivatePlan, type BillingPlan } from '../../modules/billing/api/billing.client';
import { showErrorToast, showSuccessToast } from '../../utils/errorHandler';
import { CreatePlanForm } from './components/CreatePlanForm';

function formatPrice(amount: number, currency: string, interval: string): string {
  const value = (amount / 100).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  const curr = currency?.toUpperCase() || 'USD';
  const intervalLabel = interval === 'year' ? '/year' : '/month';
  return `${curr} ${value}${intervalLabel}`;
}

interface PlanCardProps {
  plan: BillingPlan;
  onActivate: (planId: string) => Promise<void>;
  onDeactivate: (planId: string) => Promise<void>;
}

function PlanCard({ plan, onActivate, onDeactivate }: PlanCardProps) {
  const [toggling, setToggling] = useState(false);
  const limits = plan.limits ?? {};
  const features = plan.features ?? {};
  const marketingFeatures = plan.marketing_features ?? [];
  const isActive = plan.status === 'active';

  const handleToggle = async () => {
    setToggling(true);
    try {
      if (isActive) {
        await onDeactivate(plan.plan_id);
      } else {
        await onActivate(plan.plan_id);
      }
    } finally {
      setToggling(false);
    }
  };

  return (
    <Card className="h-full flex flex-col" padding="none" hover>
      <div className="p-4 sm:p-5 flex-1 flex flex-col">
        <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
          <h3 className="text-lg font-semibold text-app-text dark:text-white leading-tight">
            {plan.name}
          </h3>
          <Badge variant={isActive ? 'success' : 'gray'}>
            {plan.status}
          </Badge>
        </div>
        <p className="text-2xl font-bold text-primary-600 dark:text-primary-400 mb-3">
          {formatPrice(plan.amount, plan.currency, plan.interval)}
        </p>
        {plan.description && (
          <p className="text-sm text-app-text-muted dark:text-gray-400 mb-4 line-clamp-3">
            {plan.description}
          </p>
        )}
        {marketingFeatures.length > 0 && (
          <ul className="space-y-2 mb-4 flex-1">
            {marketingFeatures.slice(0, 5).map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-app-text dark:text-gray-200">
                <Check className="w-4 h-4 shrink-0 text-primary-500 mt-0.5" aria-hidden />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-auto pt-4 border-t border-app-border dark:border-gray-600 space-y-2">
          {limits.max_students != null && (
            <div className="flex items-center gap-2 text-sm text-app-text-muted dark:text-gray-400">
              <Users className="w-4 h-4 shrink-0" />
              <span>Up to {limits.max_students} students</span>
            </div>
          )}
          {limits.max_teachers != null && (
            <div className="flex items-center gap-2 text-sm text-app-text-muted dark:text-gray-400">
              <BookOpen className="w-4 h-4 shrink-0" />
              <span>Up to {limits.max_teachers} teachers</span>
            </div>
          )}
          {limits.max_schools != null && (
            <div className="flex items-center gap-2 text-sm text-app-text-muted dark:text-gray-400">
              <Building2 className="w-4 h-4 shrink-0" />
              <span>Up to {limits.max_schools} schools</span>
            </div>
          )}
          {Object.keys(features).length > 0 && !plan.description && (
            <p className="text-xs text-app-text-muted dark:text-gray-400 pt-1">
              {Object.entries(features)
                .filter(([, v]) => v)
                .map(([k]) => k.replace(/_/g, ' '))
                .join(' · ')}
            </p>
          )}
          <div className="pt-3">
            <Button
              type="button"
              variant={isActive ? 'outline' : 'primary'}
              size="sm"
              onClick={handleToggle}
              disabled={toggling}
              className="w-full"
            >
              {toggling ? (
                <Spinner size="sm" className="mr-2" />
              ) : isActive ? (
                <Square className="w-4 h-4 mr-2" aria-hidden />
              ) : (
                <Play className="w-4 h-4 mr-2" aria-hidden />
              )}
              {toggling ? 'Updating…' : isActive ? 'Deactivate' : 'Activate'}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}

export const PlansList: React.FC = () => {
  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const loadPlans = async () => {
    setLoading(true);
    try {
      const data = await listPlans({ limit: 100 });
      setPlans(data);
    } catch (error) {
      showErrorToast(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlans();
  }, []);

  const handleCreateSuccess = async () => {
    setIsCreateOpen(false);
    showSuccessToast('Plan created successfully.');
    await loadPlans();
  };

  const handleActivate = async (planId: string) => {
    try {
      await activatePlan(planId);
      showSuccessToast('Plan activated.');
      await loadPlans();
    } catch (error) {
      showErrorToast(error);
    }
  };

  const handleDeactivate = async (planId: string) => {
    try {
      await deactivatePlan(planId);
      showSuccessToast('Plan deactivated.');
      await loadPlans();
    } catch (error) {
      showErrorToast(error);
    }
  };

  return (
    <Layout>
      <PageHeader
        title="Plans"
        subtitle="Manage billing plans"
        actions={
          <Button onClick={() => setIsCreateOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Create
          </Button>
        }
      />

      {loading ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : plans.length === 0 ? (
        <Card>
          <CardContent padding="md">
            <div className="text-center py-12 text-app-text-muted dark:text-gray-400">
              No plans found. Create your first plan to get started.
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
          {plans.map((plan) => (
            <PlanCard
              key={plan.plan_id}
              plan={plan}
              onActivate={handleActivate}
              onDeactivate={handleDeactivate}
            />
          ))}
        </div>
      )}

      <Modal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        title="Create plan"
        size="lg"
      >
        <div className="min-w-0">
            <CreatePlanForm
            onCancel={() => setIsCreateOpen(false)}
            onSuccess={handleCreateSuccess}
          />
        </div>
      </Modal>
    </Layout>
  );
};
